import { auth } from './auth';
import toast from 'react-hot-toast';

interface RequestOptions<RequestType> {
  disableAuth?: true;
  method: 'GET' | 'DELETE' | 'POST' | 'PATCH';
  body?: RequestType;
  ignoreResponse?: true;
}

async function request<ResponseType, RequestType = ResponseType>(
  path: string,
  options: RequestOptions<RequestType>
): Promise<ResponseType> {
  const requestOptions: RequestInit = {
    method: options.method,
    body: options.body ? JSON.stringify(options.body) : undefined,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  if (!options.disableAuth) {
    const token = auth.getToken();
    if (token === null) throw new Error('Token not found');
    requestOptions.headers = {
      ...requestOptions.headers,
      authorization: `Bearer ${token}`,
    };
  }

  const baseURL = process.env.REACT_APP_API_BASE_URL || '/public';

  const url = `${baseURL}${path}`;

  const response = await fetch(url, requestOptions);
  if (!response.ok) {
    const errorResponse = await response.json();

    if (response.status === 500) {
      toast.error(`Error: could not ${options.method} ${url}`);
    }
    return Promise.reject(errorResponse);
  }
  if (options.ignoreResponse) {
    return Promise.resolve({} as ResponseType);
  }
  return await response.json();
}

async function requestSlackFile<ResponseType, RequestType = ResponseType>(
  path: string,
  options: RequestOptions<RequestType>
): Promise<any> {
  const requestOptions: RequestInit = {
    method: options.method,
    body: options.body ? JSON.stringify(options.body) : undefined,
    headers: {},
  };
  if (!options.disableAuth) {
    const token = auth.getToken();
    if (token === null) throw new Error('Token not found');
    requestOptions.headers = {
      ...requestOptions.headers,
      authorization: `Bearer ${token}`,
    };
  }
  const baseURL = process.env.REACT_APP_API_BASE_URL || '/public';

  const url = `${baseURL}${path}`;

  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    if (response.status === 500) {
      toast.error(`Error: could not ${options.method} ${url}`);
    }
    return Promise.reject(response.json());
  }
  if (options.ignoreResponse) {
    return Promise.resolve({} as ResponseType);
  }

  return await response.blob();
}

async function requestFile<ResponseType, RequestType = ResponseType>(
  path: string,
  options: any
): Promise<ResponseType> {
  const requestOptions: RequestInit = {
    method: options.method,
    body: options.body,
    headers: {},
  };

  if (!options.disableAuth) {
    const token = auth.getToken();
    if (token === null) throw new Error('Token not found');
    requestOptions.headers = {
      ...requestOptions.headers,
      authorization: `Bearer ${token}`,
    };
  }

  const baseURL = process.env.REACT_APP_API_BASE_URL || '/public';

  const url = `${baseURL}${path}`;

  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    const errorResponse = await response.json();

    if (response.status === 500) {
      toast.error(`Error: could not ${options.method} ${url}`);
    }

    console.log(response);

    return Promise.reject(errorResponse);
  }
  if (options.ignoreResponse) {
    return Promise.resolve({} as ResponseType);
  }

  return await response.json();
}

const api = {
  async post<T, R = T>(path: string, body: T, disableAuth?: true): Promise<R> {
    return await request(path, { method: 'POST', body, disableAuth });
  },
  async postFile<T, R = T>(
    path: string,
    body: any,
    disableAuth?: true
  ): Promise<R> {
    return await requestFile(path, { method: 'POST', body: body, disableAuth });
  },
  async get<T>(path: string, searchParams?: URLSearchParams): Promise<T> {
    const newPath = searchParams ? `${path}?${searchParams}` : `${path}`;

    return await request(newPath, { method: 'GET' });
  },
  async getFile<T>(path: string, searchParams?: URLSearchParams): Promise<T> {
    const newPath = searchParams ? `${path}?${searchParams}` : `${path}`;

    return await requestSlackFile(newPath, { method: 'GET' });
  },
  async patch<T, R = T>(path: string, body: Partial<T>): Promise<R> {
    return await request(path, { method: 'PATCH', body });
  },
  async delete(path: string, body?: any): Promise<void> {
    await request(path, { method: 'DELETE', body });
  },
  async deleteWithFiles(path: string, body?: any): Promise<void> {
    await requestFile(`${path}/request-delete`, { method: 'POST', body });
  },
};

export default api;
